import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  Container,
  ContainerGutter,
  CTA,
  Fieldset,
  Text,
  styled,
  TextTag,
  TokenTextAppearance
} from '@volkswagen-onehub/components-core';
import { getLang, getTranslations } from '../../translations';
import { formatDistance } from '../../utils/format';
import { useFeatureServices } from '../../use-feature-services';
import { createConstantData, createPartner } from '../../utils/get-tracking-event-data';
import { useSelectedFilterStore } from '../../store/filtersv2';
import { DealerData, Language } from '../../types';
import { useDealersStore } from '../../utils/use-dealers-store';

type Props = {
  hideLegend?: boolean;
};

const FieldsetWrapper = styled.div`
  margin-top: 12px;
  legend {
    font-size: ${props => props.theme.fonts.bigcopy[100].fontSize};
    line-height: ${props => props.theme.fonts.bigcopy[100].lineHeight};
    letter-spacing: ${props => props.theme.fonts.bigcopy[100].letterSpacing};
    color: #001E50 !important;
  }
  [aria-live] {
    margin: 0;
  }
`;

const StyledCTA = styled(CTA)`
  margin: 12px 0;
`;

const generateLabel = (name: string, distance: number, lang: Language) => {
  return (
    <Text tag={TextTag.span} appearance={TokenTextAppearance.copy200}>
      { name }, <Text tag={TextTag.span} appearance={TokenTextAppearance.copy100}> { formatDistance(distance, lang) }</Text>
    </Text>
  )
};

export const DealerList = ({ hideLegend = false }: Props) => {

  const lang = getLang();
  const { labels } = getTranslations();

  const [ allDealersSelected, setAllDealersSelected ] = useState<boolean>(false);

  const { selected, selectFilter } = useSelectedFilterStore();
  const preferredDealers = selected.dealer as string[];

  const { isLoading, dealersWithinRadius: all_dealers } = useDealersStore();

  const { tracking } = useFeatureServices();

  const handleCheckboxClick = (e: React.SyntheticEvent<HTMLInputElement>, i: number) => {
    const dealerId = e.currentTarget.value;
    let updatedPreferredDealers = preferredDealers?.includes(dealerId)
      ? preferredDealers.filter((id: string) => id !== dealerId)
      : [...preferredDealers as string[], dealerId];

    // sort by distance
    updatedPreferredDealers = updatedPreferredDealers.sort((a, b) => {
      let xDealer : DealerData = {} as DealerData;
      let yDealer : DealerData = {} as DealerData;

      (all_dealers as DealerData[])?.forEach(d => {
        if(a == d.static_dealer_id) { xDealer = d; }
        if(b == d.static_dealer_id) { yDealer = d; }
      });

      if(xDealer.distance < yDealer.distance) { return -1; }

      return 1;
    });

    // updateDealerQueryParams(updatedPreferredDealers);
    selectFilter("dealer", updatedPreferredDealers);

    if (e.currentTarget.checked) {

      const dealer = (all_dealers as DealerData[])?.filter(dealer => dealer.static_dealer_id === dealerId)[0];
      tracking.track('VWBasic_DealerButton_Click', {
        ...createConstantData(),
        content: {
          ItemCount: (all_dealers as DealerData[])?.length,
          ItemPosition: i + 1
        },
        partner: createPartner(dealer),
        link: {
          url: window.location.href,
          name: labels.dealers_preferred
        }
      });
    }
  };

  const passAllDealers = () => {
    const all_dealer_ids = (all_dealers as DealerData[])?.map(dealer => dealer.static_dealer_id);
    // updateDealerQueryParams(all_dealer_ids);
    selectFilter("dealer", all_dealer_ids);
  };

  const handleSelectAllClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (
      preferredDealers?.length < (all_dealers as DealerData[])?.length
      && !allDealersSelected
    ) {
      passAllDealers();
    } else {
      // updateDealerQueryParams([]);
      selectFilter("dealer", []);
    }
    setAllDealersSelected(!allDealersSelected);
  };

  // const updateDealerQueryParams = (dealers : string[]) => {
  //    // Update query params
  //   const objectParams = new URLSearchParams(document.location.search);
  //   if(dealers && dealers.length) {
  //     objectParams.set('dealer', dealers?.join(','));
  //   }
  //   else { objectParams.delete('dealer') }
  //   // setSearchParams(objectParams, {replace: true});
  // };

  useEffect(() => {
    if((all_dealers as DealerData[])?.length === preferredDealers?.length) {
      setAllDealersSelected(true);
    } else {
      setAllDealersSelected(false);
    }
  }, [all_dealers, preferredDealers]);

  return (
    <FieldsetWrapper>
      <Fieldset legend={hideLegend ? undefined : labels.dealers_sorted }>
        {
          isLoading
          ? null
          : <>
            <StyledCTA
              tag="button"
              emphasis="tertiary"
              onClick={handleSelectAllClick}
            >
              {
                !preferredDealers
                || preferredDealers.length === 0
                || preferredDealers.length < ((all_dealers as DealerData[])?.length || 0)
                ? labels.select_all
                : labels.deselect_all
              }
            </StyledCTA>
            <Container
              gutter={ContainerGutter.static150}
              wrap="always"
              aria-hidden="true"
            >
              { (all_dealers as DealerData[]).map((dealer, i): JSX.Element => {
                const key = `dealer-${dealer.static_dealer_id}-${i}`;
                return (
                  <Checkbox
                    key={key}
                    name="dealers"
                    value={dealer.static_dealer_id}
                    label={generateLabel(dealer.name, dealer.distance, lang)}
                    checked={preferredDealers?.includes(dealer.static_dealer_id)}
                    onClick={(e) => handleCheckboxClick(e, i)}
                  />
                )
              }) }
            </Container>
          </>
        }
      </Fieldset>
    </FieldsetWrapper>
  );
};
